import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BadgeCheck, DollarSign, TrendingUp, MapPin } from "lucide-react";

const GetPaidForBillboard = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card className="p-8">
        <div className="flex items-center gap-2 text-green-600 mb-2">
          <DollarSign size={24} />
          <span className="text-sm font-medium">Money Making Guide</span>
        </div>

        <h1 className="text-4xl font-bold mb-6">
          How to Get Paid from Billboard Advertising: Complete Money Making Guide 2024
        </h1>

        <div className="bg-green-50 p-4 rounded-lg mb-8">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="text-green-600" />
            <span className="font-semibold">Average Monthly Revenue: ₹40,000 - ₹15,00,000 per billboard</span>
          </div>
        </div>

        <div className="prose lg:prose-xl">
          {/* Introduction Section */}
          <p className="text-lg leading-relaxed mb-6">
            Billboard advertising remains one of the most lucrative investment opportunities in the marketing industry. With the right strategy and location, a single billboard can generate substantial monthly income while requiring minimal ongoing maintenance. This comprehensive guide will walk you through every step of monetizing billboard advertising space.
          </p>

          {/* Revenue Models Table */}
          <h2 className="text-2xl font-semibold mt-8 mb-4">Revenue Models</h2>
          <Table>
            <TableCaption>Billboard Revenue Models Comparison</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Model Type</TableHead>
                <TableHead>Monthly Revenue Potential</TableHead>
                <TableHead>Initial Investment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Traditional Static</TableCell>
                <TableCell>₹40,000 - ₹1,50,000</TableCell>
                <TableCell>₹5,00,000 - ₹10,00,000</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Digital Display</TableCell>
                <TableCell>₹1,00,000 - ₹15,00,000</TableCell>
                <TableCell>₹20,00,000 - ₹40,00,000</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Property Lease</TableCell>
                <TableCell>₹20,000 - ₹80,000</TableCell>
                <TableCell>Minimal</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Getting Started Steps */}
          <h2 className="text-2xl font-semibold mt-8 mb-4">Getting Started</h2>
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-green-50 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold">1</span>
                </div>
                <h3 className="font-semibold mb-2">Location Research</h3>
                <p className="text-sm">Identify high-traffic areas with strong commercial potential</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold">2</span>
                </div>
                <h3 className="font-semibold mb-2">Legal Compliance</h3>
                <p className="text-sm">Obtain necessary permits and advertising rights</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold">3</span>
                </div>
                <h3 className="font-semibold mb-2">Setup & Launch</h3>
                <p className="text-sm">Install infrastructure and begin advertising</p>
              </div>
            </div>
          </Card>

          {/* Location Strategy */}
          <h2 className="text-2xl font-semibold mt-8 mb-4">
            <MapPin className="inline-block mr-2" />
            Prime Locations for Maximum Revenue
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="p-4">
              <h3 className="font-semibold mb-2">High-Traffic Areas</h3>
              <ul className="list-none space-y-2">
                <li className="flex items-center">
                  <BadgeCheck className="mr-2 text-green-500" />
                  Highway intersections
                </li>
                <li className="flex items-center">
                  <BadgeCheck className="mr-2 text-green-500" />
                  City centers
                </li>
                <li className="flex items-center">
                  <BadgeCheck className="mr-2 text-green-500" />
                  Commercial districts
                </li>
              </ul>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Strategic Locations</h3>
              <ul className="list-none space-y-2">
                <li className="flex items-center">
                  <BadgeCheck className="mr-2 text-green-500" />
                  Shopping malls
                </li>
                <li className="flex items-center">
                  <BadgeCheck className="mr-2 text-green-500" />
                  Entertainment zones
                </li>
                <li className="flex items-center">
                  <BadgeCheck className="mr-2 text-green-500" />
                  Transit hubs
                </li>
              </ul>
            </Card>
          </div>

          {/* AdBoardBooking.com Integration */}
          <Alert className="my-8 bg-blue-50">
            <AlertTitle className="text-blue-800">
              Maximize Your Billboard Revenue with AdBoardBooking.com
            </AlertTitle>
            <AlertDescription>
              Streamline your billboard management and increase revenue with our automated booking platform. Get started today with zero platform fees for the first 3 months.
            </AlertDescription>
          </Alert>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-blue-500 to-green-500 text-white p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Ready to Start Earning?</h3>
            <p className="mb-4">
              Join thousands of successful billboard owners on AdBoardBooking.com and start maximizing your advertising revenue today.
            </p>
            <div className="flex gap-4">
              <a href="https://www.adboardbooking.com/publisher">
                <Button className="bg-white text-blue-600 hover:bg-blue-50">
                Sign Up Now
                </Button>
              </a>
              
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GetPaidForBillboard;